/* eslint-disable no-param-reassign */
import { init, echarts as standardChartEcharts } from '@/standardChartManager/standardChart';
import type { ExtensionApi } from './extension';
import { View } from './View';
import type {
  StandardChartLayer,
  StandardChartLayerSpec,
  StandardChartLayerRenderResult
} from './layer';
import { getLayer } from '../layer';
import {
  type Option as StandardChartOption,
  type StandardChartIns,
  normalizeOption as normalizeStandardChartOption,
  findOptionByType as findStandardChartOptionByType,
  mergeComponentsOption as mergeStandardChartComponentsOption,
  mergeSeriesOption as mergeStandardChartSeriesOption,
  matchMergeOption as matchMergeStandardChartOption,
  mergeOption,
  type PlayOpts
} from '../helper/standardChart';
import type { Insight } from '../insight/insightManager';

const chartDomToAPI = new WeakMap<HTMLElement, ExtensionApi>();

export function getApiByChartDom(dom: HTMLElement) {
  return chartDomToAPI.get(dom);
}

export type LifecycleEvent = {
  'standardchart:play': (
    chartIns: StandardChartIns,
    legendIns: InstanceType<ExtensionApi['ui']['Legend']['Legend']>,
    ...params: Parameters<StandardChartIns['play']>
  ) => void;
};

export interface Spec extends Omit<StandardChartOption, 'series'> {
  /** 图层，类似 echarts 中的 series 概念，但比 series 抽象程度更高 */
  layers: StandardChartLayerSpec[];
  dvInsight?: Insight[];
}

function matchMergeLayersUpdateOption(
  layers: Spec['layers'],
  series: Extract<StandardChartOption['series'], unknown[]>
): Spec['layers'] {
  return layers.map(layer => {
    const sameTypeSeries = findStandardChartOptionByType(layer, series);
    if (!sameTypeSeries) {
      return layer;
    }

    const { type, encoding, dataIndex, ...overrideOption } = sameTypeSeries;

    return mergeStandardChartSeriesOption(
      layer as unknown as Exclude<StandardChartOption['series'], unknown[]>,
      overrideOption as unknown as Exclude<StandardChartOption['series'], unknown[]>,
      {
        override: true
      }
    ) as unknown as typeof layer;
  });
}

function matchMergeInsightsUpdateOption(
  oldInsight: Spec['dvInsight'],
  newInsight: Spec['dvInsight'],
  option = { override: true }
): Spec['dvInsight'] {
  return mergeOption(oldInsight, newInsight, option) as Spec['dvInsight'];
}

export function mergeUpdateOption(spec: Spec, updateOption: StandardChartOption): Spec {
  const { layers, dvInsight: oldInsight = [], ...oldCommonOption } = spec;
  const { series = [], dvInsight: newInsight = [], ...newCommonOption } = updateOption;

  return {
    ...matchMergeStandardChartOption(
      oldCommonOption as StandardChartOption,
      newCommonOption as StandardChartOption,
      { override: true }
    ),
    layers: matchMergeLayersUpdateOption(layers, Array.isArray(series) ? series : [series]),
    dvInsight: matchMergeInsightsUpdateOption(oldInsight, newInsight)
  };
}

/**
 * * layer 类比于 series，允许用户配置覆盖到生成的 series 配置中
 */
function overrideLayersOption(
  options: StandardChartLayerRenderResult[],
  overrideOptions: StandardChartLayerSpec[]
): StandardChartLayerRenderResult[] {
  const layersOption = [] as StandardChartLayerRenderResult[];

  overrideOptions.forEach((layer, index) => {
    const { dataIndex, encoding, ...overrideOption } = layer;
    const result = options[index];

    result.series = result.series.map(item => {
      return mergeStandardChartSeriesOption(item, overrideOption, {
        override: true
      });
    });

    layersOption[index] = result;
  });

  return layersOption;
}

/**
 * 处理有限的情况
 * - 公共组件基本以第一个图表的为主
 * - 轴会进行判断，非重复的轴会被添加
 *
 * ! 目前支持叠加的仅 bar / line / pie
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function combineLayersOption(
  renderOptions: StandardChartLayerRenderResult[]
): StandardChartLayerRenderResult {
  if (renderOptions.length > 1) {
    renderOptions.forEach(item => {
      const error = normalizeStandardChartOption(item).xAxis.some(
        axis => typeof axis.__dataIndex === 'undefined' || typeof axis.__encodeField === 'undefined'
      );

      if (error) {
        throw new Error('Missing axis.__dataIndex and axis.__encodeField fields.');
      }
    });
  }

  const result = normalizeStandardChartOption(renderOptions[0]);

  renderOptions.slice(1).forEach(item => {
    const normalizedOption = normalizeStandardChartOption(item);
    const axisIndexMap = new Map<'x' | 'y', Map<number, number>>();
    axisIndexMap.set('x', new Map());
    axisIndexMap.set('y', new Map());

    normalizedOption.xAxis.forEach((axis, index) => {
      const idx = result.xAxis.findIndex(
        axis_ =>
          axis_.__dataIndex === axis.__dataIndex && axis_.__encodeField === axis.__encodeField
      );

      if (idx > -1) {
        axisIndexMap.get('x')!.set(index, idx);
      } else {
        const length = result.xAxis.push(axis);

        axisIndexMap.get('x')!.set(index, length - 1);
      }
    });

    normalizedOption.yAxis.forEach((axis, index) => {
      const idx = result.yAxis.findIndex(
        axis_ =>
          axis_.__dataIndex === axis.__dataIndex &&
          // 一个y轴可能对应多个数据字段，由数组组成
          JSON.stringify(axis_.__encodeField) === JSON.stringify(axis.__encodeField)
      );

      if (idx > -1) {
        axisIndexMap.get('y')!.set(index, idx);
      } else {
        const length = result.yAxis.push(axis);
        axisIndexMap.get('y')!.set(index, length - 1);
      }
    });

    normalizedOption.series = (
      normalizedOption.series as unknown as { xAxisIndex: number; yAxisIndex: number }[]
    ).map(seriesItem => ({
      ...seriesItem,
      xAxisIndex: axisIndexMap.get('x')!.get(seriesItem.xAxisIndex || 0),
      yAxisIndex: axisIndexMap.get('y')!.get(seriesItem.yAxisIndex || 0)
    })) as [];

    (result.series as []).push(...(normalizedOption.series as []));
    result?.legend?.data.push(...(normalizedOption?.legend?.data || []));
  });

  return result;
}

/**
 * * 允许公共组件（series 除外）的用户配置覆盖到生成的配置中
 */
function overrideComponentsOption(
  option: StandardChartLayerRenderResult,
  overrideOption: Omit<Spec, 'layers'>
): StandardChartLayerRenderResult {
  return mergeStandardChartComponentsOption(option, overrideOption, {
    override: true
  }) as StandardChartLayerRenderResult;
}

function postProcess(api: ExtensionApi, option: StandardChartLayerRenderResult) {
  const seriesColorMapByName = new Map<string, string>();

  // * series 配色逻辑（默认循环色板）
  if (option.color?.length) {
    let colorIndex = 0;
    option.series.forEach(seriesItem => {
      if (['line', 'bar'].includes(seriesItem.type)) {
        if (typeof seriesItem?.itemStyle?.color === 'undefined') {
          seriesItem.itemStyle = {
            ...(seriesItem.itemStyle || {}),
            color: option.color[colorIndex]
          };

          colorIndex = (colorIndex + 1) % option.color.length;
          seriesColorMapByName.set(seriesItem.name, seriesItem.itemStyle.color!);
        }
      } else if (['pie'].includes(seriesItem.type)) {
        seriesItem.data?.forEach((dataItem: any) => {
          if (dataItem.name && typeof dataItem?.itemStyle?.color === 'undefined') {
            dataItem.itemStyle = { ...(dataItem.itemStyle || {}), color: option.color[colorIndex] };

            colorIndex = (colorIndex + 1) % option.color.length;
            seriesColorMapByName.set(dataItem.name, dataItem.itemStyle.color!);
          }
        });
      }
    });
  }

  // * 调整图例的配色与 series 保持一致
  option?.legend?.data?.forEach(item => {
    if (item.name) {
      item.color = seriesColorMapByName.get(item.name) || item.color;
    }
  });
}

/**
 * 统一处理legend中series的显隐
 */
function processLegendRevealing(renderOption: StandardChartLayerRenderResult) {
  const { legend } = renderOption;
  const chartLegendOps = {
    show: false,
    selected: {}
  } as {
    show: boolean;
    selected: {
      [key: string]: boolean;
    };
  };
  legend?.data?.forEach(item => {
    chartLegendOps.selected[item.name as string] = item.selected;
  });

  const { merge } = standardChartEcharts.zrUtil;
  // eslint-disable-next-line no-param-reassign
  renderOption.legend = merge(legend, chartLegendOps);
}

function processSeriesLegend(
  api: ExtensionApi,
  series: StandardChartLayerRenderResult['series'][0],
  index: number,
  optionColor: StandardChartLayerRenderResult['color']
) {
  const { util } = api;
  const disableArray = ['bar3D'];
  const color = series.itemStyle?.color || optionColor;
  return {
    name: series.name,
    type: util.const.SERIES_2_LEGEND_TYPE_MAP[series.type],
    color: Array.isArray(color) ? color[index % color.length] : color,
    selected: true,
    disableSelect: disableArray.includes(series.type)
  };
}

function processSeriesDataLegend(
  api: ExtensionApi,
  series: StandardChartLayerRenderResult['series'][0],
  optionColor: StandardChartLayerRenderResult['color']
) {
  const { util } = api;
  const disableArray = ['bar3D', 'scatter3D', 'treemap', 'dvScatter'];
  return series.data.map((item, index) => {
    const color = Array.isArray(item.itemStyle)
      ? item.itemStyle[0].color
      : item.itemStyle?.color || optionColor;
    return {
      name: item.name,
      type: util.const.SERIES_2_LEGEND_TYPE_MAP[series.type],
      color: Array.isArray(color) ? color[index % color.length] : color,
      selected: true,
      disableSelect: disableArray.includes(series.type)
    };
  });
}

function processLegend(api: ExtensionApi, option: StandardChartLayerRenderResult) {
  if (option.legend?.data) {
    return;
  }
  const seriesDataArray = ['dvScatter', 'scatter', 'treemap', 'pie', 'scatter3D', 'graph', 'radar'];
  const data = [];
  option.series.forEach((series, index) => {
    if (seriesDataArray.includes(series.type)) {
      data.push(...processSeriesDataLegend(api, series, option.color));
    } else {
      data.push(processSeriesLegend(api, series, index, option.color));
    }
  });
  const legend = {
    data
  };
  option.legend = {
    ...option.legend,
    ...legend
  };
}

function mergeChartOption(
  api: ExtensionApi,
  spec: Spec,
  renderOptions: StandardChartLayerRenderResult[]
): StandardChartOption {
  // ! 有意将 series 字段排除掉，防止用户误传
  const { series, layers = [], ...componentOption } = spec;
  const { proxy } = api.spec;
  const presetOption = api.hook.buildStandardChartPresetOption(api);
  let renderOption;

  // * 1. 将每一层的用户配置覆盖到默认配置
  renderOption = overrideLayersOption(renderOptions, layers);

  // * 2. 合并层与层之间的配置项
  renderOption = combineLayersOption(renderOption);
  // 先将 token 映射后的配置项作为默认值进行合并
  if (!proxy) {
    renderOption = matchMergeStandardChartOption(
      renderOption,
      presetOption
    ) as unknown as typeof renderOption;
  }

  // * 3. 将用户组件配置覆盖到当前配置(双y轴配置需要保证用户配置的yAxis数组配置对应双y轴)
  renderOption = overrideComponentsOption(renderOption, componentOption);

  // * 4. 后处理
  postProcess(api, renderOption);
  if (!proxy) {
    // * 5. 更新图例
    processLegend(api, renderOption);
    processLegendRevealing(renderOption);
  }

  return renderOption;
}

function seriesDataCallback(series: StandardChartOption['series'][]) {
  series.forEach(seriesItem => {
    const { dvData } = seriesItem as unknown as { dvData: (data: unknown) => void };
    dvData?.((seriesItem as { data: unknown }).data);
  });
}

function optionCallback(renderOptions: Partial<StandardChartOption>) {
  if ((renderOptions?.series as []).length) {
    seriesDataCallback(renderOptions.series as []);
  }
}

export class StandardChartView extends View {
  legendIns!: InstanceType<ExtensionApi['ui']['Legend']['Legend']>;

  chartIns!: StandardChartIns;

  timelineIns!: { destroy: () => void };

  private __layoutInfo = {} as {
    timelineTheme: string;
    showLegend: boolean;
    showDropdownTimeline: boolean;
    showTimeline: boolean;
  };

  private __domSet = {} as {
    $container: HTMLElement;
    $toolbar: HTMLElement;
    $toolbarLegend: HTMLElement;
    $toolbarDropdownTimeline: HTMLElement;
    $chart: HTMLElement;
    $timeline: HTMLElement;
  };

  private __renderOption!: StandardChartOption;

  /** 用来和副图进行联动交互，默认选中的股票 */
  selectedStockIndex = 0;

  /**
   * 获取表示日期的类目轴数据，用来和副图进行联动交互
   */
  getDateAxisData(api: ExtensionApi) {
    const { xAxis = [], yAxis = [] } = this.__renderOption;

    const dateXAxis = (Array.isArray(xAxis) ? xAxis : [xAxis]).find(
      item => item.type === 'category' && api.util.data.isValidDateString(item?.data?.[0] as string)
    );
    if (dateXAxis) {
      return dateXAxis.data || [];
    }

    const dateYAxis = (Array.isArray(yAxis) ? yAxis : [yAxis]).find(
      item => item.type === 'category' && api.util.data.isValidDateString(item?.data?.[0] as string)
    );
    if (dateYAxis) {
      return dateYAxis.data || [];
    }

    return [];
  }

  /**
   * 获取 dataZoom 相关数据，用来和副图进行联动交互
   */
  getDataZoomData() {
    const { dataZoom = [] } = this.__renderOption;

    return (Array.isArray(dataZoom) ? dataZoom : [dataZoom]).find(item => item.type === 'slider');
  }

  /**
   * 获取渲染的option
   */
  private __getRenderOption() {
    return this.__renderOption;
  }

  private __layout(api: ExtensionApi, spec: Spec) {
    const timelineTheme =
      api.token?.dvTimeline?.theme ||
      (api.layout === api.util.const.LAYOUT.SMALL_2_1 ? 'pc-simple' : 'pc');
    const hasTimeline = spec.layers.some(
      layer => (getLayer(layer.type) as StandardChartLayer).withTimeline === true
    );
    const showLegend = true;
    const showDropdownTimeline = hasTimeline && api.layout === api.util.const.LAYOUT.SMALL;
    const showTimeline = hasTimeline && !showDropdownTimeline;

    this.__layoutInfo = {
      timelineTheme,
      showLegend,
      showDropdownTimeline,
      showTimeline
    };
  }

  private __initDom(api: ExtensionApi, $container: HTMLElement) {
    if (Object.keys(this.__domSet).length === 0) {
      this.__buildDom(api, $container);
    }
  }

  private __buildDom(api: ExtensionApi, $container: HTMLElement) {
    const { showLegend, showDropdownTimeline, showTimeline } = this.__layoutInfo;
    const showToolbar = showLegend || showDropdownTimeline;

    // * build --------------------------------------
    //
    // eslint-disable-next-line no-param-reassign
    $container.style.cssText +=
      'display: flex; flex-direction: column; align-items: center;justify-content: center;';

    //
    const $toolbar = document.createElement('section');
    const $toolbarLegend = document.createElement('section');
    const $toolbarDropdownTimeline = document.createElement('section');
    $toolbar.style.cssText =
      'width: 100%; margin-bottom: 8px; display: flex; flex-direction: row; align-items: center; justify-content: flex-start;';
    $toolbarLegend.style.cssText = 'flex: 1; width: 0;';
    $toolbarDropdownTimeline.style.cssText = 'margin-left: auto;';

    //
    const $chart = document.createElement('section');
    $chart.style.cssText = 'width: 100%; flex: 1; height: 0;';

    //
    const $timeline = document.createElement('section');

    $timeline.style.cssText = `width: 100%; margin-top: 8px; ${api.token?.dvTimeline?.domCSSText}`;
    $timeline.style.backgroundColor = api.token.backgroundColor!;

    // * mount --------------------------------------
    if (showLegend) {
      $toolbar.appendChild($toolbarLegend);
    }
    if (showDropdownTimeline) {
      $toolbar.appendChild($toolbarDropdownTimeline);
    }

    if (showToolbar) {
      $container.appendChild($toolbar);
    }
    $container.appendChild($chart);
    if (showTimeline) {
      $container.appendChild($timeline);
    }

    // debug id
    $toolbar.setAttribute('__aigc-vis-id', 'toolbar');
    $toolbarLegend.setAttribute('__aigc-vis-id', 'toolbarLegend');
    $toolbarDropdownTimeline.setAttribute('__aigc-vis-id', 'toolbarDropdownTimeline');
    $chart.setAttribute('__aigc-vis-id', 'chart');
    $timeline.setAttribute('__aigc-vis-id', 'timeline');

    this.__domSet = {
      $container,
      $toolbar,
      $toolbarLegend,
      $toolbarDropdownTimeline,
      $chart,
      $timeline
    };
  }

  private __renderLegend(
    api: ExtensionApi,
    data: StandardChartLayerRenderResult['legend']['data']
  ) {
    const { proxy } = api.spec;

    if (!data || proxy) {
      return;
    }

    const globalLayoutInfo = api.hook.layout(api);
    const legendData = data.slice();

    if (globalLayoutInfo.secondaryView.enable && globalLayoutInfo.secondaryView.hasSimpleKLine) {
      legendData.push({
        type: api.ui.Legend.ShowType.KLine,
        stocks: api.spec.view.secondary!.layers[0].stocks,
        defaultValueIndex: this.selectedStockIndex
      });
    }

    if (!this.__layoutInfo.showLegend || legendData.length === 0) {
      if (!this.__layoutInfo.showDropdownTimeline) {
        this.__domSet.$toolbar.style.display = 'none';
      }

      return this.legendIns;
    }

    this.legendIns = new api.ui.Legend.Legend(this.__domSet.$toolbarLegend, {
      data: legendData,
      layout: api.token.dvStandardChart?.baseOption?.legend?.dvLayout || 'pagination',
      token: api.token
    });

    return this.legendIns;
  }

  private __renderChart(
    api: ExtensionApi,
    renderParams: { option: Partial<StandardChartOption>; opts?: PlayOpts },
    initParams?: { useECharts?: boolean; domContainer?: HTMLElement; theme?: string }
  ) {
    const $dom = initParams?.domContainer || this.__domSet.$chart;
    let chartIns: StandardChartIns;
    // debugger;

    if (initParams?.useECharts) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const ecIns = echarts.init($dom, api.token);
      chartIns = {
        play(params) {
          ecIns.setOption(params?.option, params?.opts);
        },
        getECharts: () => ecIns,
        destroy() {
          ecIns.dispose();
        }
      };
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      chartIns = this.chartIns || init($dom, api.theme || initParams?.theme, api.token, {});
    }

    console.log('图表的实例', chartIns);

    // 处理回调
    optionCallback(renderParams.option);
    api.hook.beforeRenderStandardChart(api, renderParams, chartIns, this.legendIns);

    // * 将 echarts 实例与上层组件库实例关联起来
    chartDomToAPI.set($dom, api);

    api.lifecycle.emit('standardchart:play', chartIns, this.legendIns, renderParams);
    renderParams.opts = renderParams.opts || { notMerge: true };
    chartIns.play(renderParams);

    this.__renderOption = renderParams.option as StandardChartOption;
    this.chartIns = chartIns;

    api.hook.triggerLegendUpdater(this.chartIns, this.legendIns);

    return this.chartIns;
  }

  private __renderTimeline(api: ExtensionApi, data: string[], spec?: Spec['layers'][0]) {
    const { token, helper, util, ui, hook } = api;

    if (this.__layoutInfo.showTimeline) {
      // * 优先级：当前配置 > 自定义 token > ued token
      let renderParams = {
        dataIndex: data.length - 1,
        // ! 动画保证和图表时间一致
        animation: {
          intervalTime: spec?.animationDuration
        }
      };
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      renderParams = util.data.deepMerge(renderParams, token?.dvTimeline?.config);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      renderParams = util.data.deepMerge(
        renderParams,
        util.data.deepFilterUndefined(helper.timeline.uedTokenToOption(token))
      );

      hook.beforeRenderTimeline(api, renderParams);

      if (window.ThsDataVTimeline) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        this.timelineIns = new ThsDataVTimeline.Timeline(this.__domSet.$timeline, {
          theme: this.__layoutInfo.timelineTheme,
          data,
          config: renderParams
        });
      } else {
        console.warn('动态AIGC组件需要额外引入ThsDataVTimeline以支持时间轴功能');
      }
    }

    if (this.__layoutInfo.showDropdownTimeline) {
      this.timelineIns = new ui.Dropdown(this.__domSet.$toolbarDropdownTimeline, {
        theme: 'default',
        data: data.map((date, index) => ({ index, key: String(date), text: date })),
        defaultValue: String(data[data.length - 1]),
        listMaxHeight: this.__domSet.$container.getBoundingClientRect().height * 0.5,
        token: token?.dvDropdown
      });
    }

    return this.timelineIns;
  }

  /**
   * 层的渲染逻辑分为两种：
   *
   * - 多个允许叠加的层，会走全局统一的渲染逻辑，具体层的渲染函数实现只需要返回图表配置项即可，然后会对多个层的配置项进行合并
   * - 单个不允许叠加的层，具体层的实现会根据需要允许做差异化实现，而不是走全局统一逻辑，这个时候层的渲染函数需要返回一个回调函数，其参数为全局的渲染代理函数
   */
  private __renderLayers(api: ExtensionApi, spec: Spec) {
    const globalLayoutInfo = api.hook.layout(api);
    let { layers = [] } = spec;
    const renderResults = [] as StandardChartLayerRenderResult[];

    // 默认值
    layers.forEach(layer => {
      layer.dataIndex = layer.dataIndex || 0;
      layer.animationDuration = layer.animationDuration || 1000;
    });

    if (globalLayoutInfo.mainView.hasIndependentLayer) {
      if (layers.length > 1) {
        throw new Error('Multiple `independent` layers are not supported.');
      }

      //
      const firstLayer = layers[0];
      const layerDefine = getLayer(firstLayer.type) as StandardChartLayer;

      const renderResultTemp = layerDefine.render(api, firstLayer, this.__domSet.$chart);

      if (typeof renderResultTemp === 'function') {
        renderResultTemp({
          mergeChartOptionProxy: option => mergeChartOption(api, spec, [option]),
          renderTimelineProxy: data => this.__renderTimeline(api, data, firstLayer),
          renderLegendProxy: data => this.__renderLegend(api, data),
          renderChartProxy: (...args) => this.__renderChart(api, ...args),
          cacheRenderOptionProxy: option => {
            this.__renderOption = option as StandardChartOption;
            api.hook.triggerLegendUpdater(this.chartIns, this.legendIns);
          },
          getRenderOption: () => this.__getRenderOption(),
          optionCallbackProxy: option => optionCallback(option)
        });

        if (!this.chartIns) {
          throw new Error(
            'One of `renderChartProxy()` and `cacheChartInsRefProxy()` must be called or a memory leak will occur.'
          );
        }

        return [];
      }

      renderResults.push(renderResultTemp as StandardChartLayerRenderResult);
      layers = layers.slice(1);
    }

    layers.forEach(layer => {
      const layerDefine = getLayer(layer.type) as StandardChartLayer;

      renderResults.push(
        layerDefine.render(api, layer, this.__domSet.$chart) as StandardChartLayerRenderResult
      );
    });

    return renderResults;
  }

  render(api: ExtensionApi, $container: HTMLElement, spec: Spec) {
    this.__layout(api, spec);
    this.__initDom(api, $container);

    const { layers = [] } = spec;
    // 如果encoding内映射的字段在原始数据中不存在，则不进行渲染
    if (
      layers.some(
        layer => typeof layer.__isEncodingValid !== 'undefined' && !layer.__isEncodingValid
      )
    ) {
      return;
    }
    const renderResults = this.__renderLayers(api, spec);

    if (renderResults.length === 0) {
      return;
    }

    const option = mergeChartOption(api, spec, renderResults);
    const { legend = {}, ...renderOption } = option;

    // ! 渲染顺序不要改变
    this.__renderTimeline(api, renderOption.xAxis[0]?.data || [], layers[0]);
    this.__renderLegend(api, legend?.data);
    this.__renderChart(api, { option });
  }

  resize() {
    if (!this.chartIns) {
      return;
    }

    this.chartIns?.getECharts()?.resize();
  }

  destroy() {
    this.legendIns?.destroy?.();
    this.timelineIns?.destroy?.();
    this.chartIns?.destroy?.();
  }
}
